import { FC, useState, useRef, useEffect } from "react";
import type { StochasticOscillatorSettings, StochasticMAType } from "../../model";
import styles from "./AcceleratorOscillatorInputsTab.module.css";

const MA_OPTIONS: StochasticMAType[] = ["SMA", "WMA", "EMA", "SMMA", "TMA"];

type InputKey = "kPeriod" | "dPeriod" | "smoothingPeriod";
const FIELDS: { key: InputKey; label: string }[] = [
  { key: "kPeriod", label: "%K Period" },
  { key: "dPeriod", label: "%D Period" },
  { key: "smoothingPeriod", label: "Smoothing" },
];

interface Props {
  settings: StochasticOscillatorSettings;
  onChange: (s: StochasticOscillatorSettings) => void;
}

export const StochasticOscillatorInputsTab: FC<Props> = ({ settings, onChange }) => {
  const [values, setValues] = useState<Record<InputKey, string>>({
    kPeriod: String(settings.kPeriod),
    dPeriod: String(settings.dPeriod),
    smoothingPeriod: String(settings.smoothingPeriod),
  });
  const [maOpen, setMaOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!maOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setMaOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [maOpen]);

  const handleBlur = (key: InputKey) => {
    const parsed = parseInt(values[key], 10);
    const clamped = isNaN(parsed) ? settings[key] : Math.min(Math.max(1, parsed), 100);
    setValues((prev) => ({ ...prev, [key]: String(clamped) }));
    onChange({ ...settings, [key]: clamped });
  };

  const selectMA = (ma: StochasticMAType) => {
    onChange({ ...settings, movingAverage: ma });
    setMaOpen(false);
  };

  return (
    <div className={styles.tab}>
      {FIELDS.map(({ key, label }) => (
        <div key={key} className={styles.row}>
          <span className={styles.label}>{label}</span>
          <input
            type="number"
            className={styles.input}
            min={1}
            max={100}
            value={values[key]}
            onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
            onBlur={() => handleBlur(key)}
          />
        </div>
      ))}
      <div className={styles.row}>
        <span className={styles.label}>Moving Average</span>
        <div className={styles.selectWrap} ref={dropdownRef}>
          <button className={styles.select} onClick={() => setMaOpen((prev) => !prev)}>
            <span>{settings.movingAverage}</span>
            <span className={`${styles.chevron} ${maOpen ? styles.chevronUp : ""}`}>▾</span>
          </button>
          {maOpen && (
            <div className={styles.dropdown}>
              {MA_OPTIONS.map((opt) => (
                <button key={opt} className={styles.option} onClick={() => selectMA(opt)}>
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
